/**
 * Email Preference Center Types
 *
 * Shared types for the email preference center system that allows users
 * to manage their email subscription preferences via token-based URLs.
 */

import { coerceTimestampToMs } from './firestore-timestamp.js';
import type { AuditEmailFrequency } from './project.js';

// ======================================================================================|
// MARK: Core Types

export type EmailCategory =
  | "lifecycle"
  | "announcement"
  | "educational"
  | "promotional"
  | "sequence"
  | "transactional"
  | "product"
  | "operational";

export const EMAIL_CATEGORIES: EmailCategory[] = [
  "lifecycle",
  "announcement",
  "educational",
  "promotional",
  "sequence",
  "transactional",
  "product",
  "operational",
];

export const EMAIL_CATEGORY_LABELS: Record<
  EmailCategory,
  { name: string; description: string }
> = {
  lifecycle: {
    name: "Account & Trial",
    description:
      "Trial reminders, account status updates, and onboarding tips",
  },
  announcement: {
    name: "Product Updates",
    description: "New features, improvements, and release notes",
  },
  educational: {
    name: "ASO Tips & Guides",
    description:
      "App Store Optimization best practices and educational content",
  },
  promotional: {
    name: "Offers & Promotions",
    description: "Special offers, discounts, and promotional campaigns",
  },
  sequence: {
    name: "Drip Sequences",
    description: "Automated multi-step email sequences",
  },
  transactional: {
    name: "Transactional",
    description: "Receipts, confirmations, and system notifications",
  },
  product: {
    name: "Product Emails",
    description: "Emails related to product activity and usage",
  },
  operational: {
    name: "Operational",
    description: "Service alerts, maintenance notices, and policy updates",
  },
};

// ======================================================================================|
// MARK: Firestore Document

/** Which surface wrote an email-preference change (A7Y-4964). */
export type EmailPreferenceChangeChannel =
  | "preference_center"
  | "one_click"
  | "unsubscribe_page"
  | "admin"
  // A7Y-5192: the opt-in checkbox on the quick-audit form.
  | "quick_audit_form";

export interface EmailPreferenceChangeEntry {
  timestamp: string;
  source: "user" | "admin" | "migration";
  changes: string;
  ip?: string;
  /** The surface that wrote the change. */
  channel?: EmailPreferenceChangeChannel;
  /** True only when the change newly opted the address out of something. */
  unsubscribed?: boolean;
}

/**
 * Firestore document for email preferences, keyed by normalized email address.
 * Collection: EmailPreferences
 */
export interface EmailPreferencesDoc {
  email: string;
  optedOut: Partial<Record<EmailCategory, boolean>>;
  globalUnsubscribe: boolean;
  updatedAt: string;
  auditLog: EmailPreferenceChangeEntry[];
  /** A7Y-5192: follow-up consent given by an address, per workflow. */
  followUpConsent?: {
    quick_audit_followup?: QuickAuditFollowupConsent;
  };
}

/** A7Y-5192: what a quick-audit lead agreed to, and when. */
export interface QuickAuditFollowupConsent {
  consentedAt: string;
  /** The audit whose report the lead asked for when they ticked the box. */
  auditId: string;
  /** The checkbox wording the lead saw. */
  wording: string;
}

// ======================================================================================|
// MARK: API Request/Response Types

export interface EmailPreferencesGetResponse {
  email: string;
  preferences: Partial<Record<EmailCategory, boolean>>;
  globalUnsubscribe: boolean;
}

export interface EmailPreferencesUpdateRequest {
  token: string;
  preferences?: Partial<Record<EmailCategory, boolean>>;
  globalUnsubscribe?: boolean;
}

export interface EmailPreferencesUpdateResponse {
  success: boolean;
  email: string;
}

export interface GeneratePreferenceLinkRequest {
  email: string;
}

export interface GeneratePreferenceLinkResponse {
  url: string;
  email: string;
}

// ======================================================================================|
// MARK: Email Cadence (A7Y-3372)

/**
 * One-click cadence-change request body posted from the public
 * `/email-cadence` confirmation page. `cadence` accepts all five
 * `AuditEmailFrequency` values (including `"per-run"`, reachable via Undo).
 */
export interface EmailCadenceApplyRequest {
  token: string;
  cadence: AuditEmailFrequency;
}

export interface EmailCadenceApplyResponse {
  success: true;
  projectId: string;
  orgId: string;
  projectName: string;
  storeSlug: string;
  appliedCadence: AuditEmailFrequency;
  previousCadence: AuditEmailFrequency;
}

// ======================================================================================|
// MARK: Admin Types

export interface AdminEmailSuppressionEntry {
  email: string;
  globalUnsubscribe: boolean;
  optedOut: Partial<Record<EmailCategory, boolean>>;
  updatedAt: string;
}

export interface AdminEmailSuppressionListResponse {
  data: AdminEmailSuppressionEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ======================================================================================|
// MARK: Delivery Suppression (A7Y-4964)

/** Transient-bounce count at/above which a recipient is suppressed. */
export const SUPPRESSION_TRANSIENT_THRESHOLD = 3;

/** Freshness window for transient-bounce suppression. source: moved unchanged from a7y-functions/src/common/email_suppression_check.ts:27 (14 days). */
export const SUPPRESSION_TRANSIENT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

export type DeliverySuppressionReason = "complaint" | "unsubscribe" | "bounce";

/**
 * The fields of an `EmailSuppressions/<email>` document that decide delivery.
 * `lastBounceAt` is raw Firestore data (Timestamp, Date, string or number).
 */
export interface EmailSuppressionRecord {
  reason?: string;
  permanentBounce?: boolean;
  transientBounceCount?: number;
  lastBounceAt?: unknown;
}

/**
 * Why delivery to this address is suppressed, or null when it is not.
 * Precedence: complaint, then bounce, then unsubscribe. A permanent bounce is
 * sticky; transient bounces suppress only at the threshold while the latest
 * bounce is inside the freshness window.
 */
export function deliverySuppressionOf(
  record: EmailSuppressionRecord | null | undefined,
  nowMs: number,
): DeliverySuppressionReason | null {
  if (!record) return null;
  if (record.reason === "complaint") return "complaint";
  // A bounce outranks an unsubscribe: an address that cannot receive mail must
  // not get payment notices just because it also unsubscribed.
  if (record.permanentBounce === true) return "bounce";
  if ((record.transientBounceCount ?? 0) >= SUPPRESSION_TRANSIENT_THRESHOLD) {
    const lastMs = coerceTimestampToMs(record.lastBounceAt);
    if (lastMs !== null && nowMs - lastMs <= SUPPRESSION_TRANSIENT_WINDOW_MS) {
      return "bounce";
    }
  }
  if (record.reason === "unsubscribe") return "unsubscribe";
  return null;
}

/**
 * Whether an automatic email of `category` may be sent to an address.
 * A complaint or bounce stops every category; transactional otherwise passes;
 * then an unsubscribe suppression, the global unsubscribe and the per-category
 * opt-out each stop the send.
 */
export function isEmailCategoryAllowed(
  prefs: Pick<EmailPreferencesDoc, "globalUnsubscribe" | "optedOut"> | null | undefined,
  suppression: DeliverySuppressionReason | null,
  category: EmailCategory,
): boolean {
  if (suppression === "complaint" || suppression === "bounce") return false;
  if (category === "transactional") return true;
  if (suppression === "unsubscribe") return false;
  if (prefs?.globalUnsubscribe === true) return false;
  if (prefs?.optedOut?.[category] === true) return false;
  return true;
}
